import { useState } from "react";
import type { MetricsResult } from "../engine/types";

/**
 * Terminal-pro headline tiles: RTP hero (wider, accent-tinted, CI line)
 * plus hit-frequency / volatility / max-win tiles, with a
 * reproducibility footer (copyable config_hash · schema · spin count).
 */
export function MetricsPanel({
  metrics,
  streaming = false,
}: {
  metrics: MetricsResult;
  streaming?: boolean;
}) {
  return (
    <section>
      <header className="mb-2 flex items-baseline justify-between">
        <h2 className="flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-paper-bright dark:text-term-bright">
          Headline metrics
          {streaming && (
            <span className="inline-flex items-center gap-1.5 border border-emerald-300 bg-emerald-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-400">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />
              streaming
            </span>
          )}
        </h2>
        <p className="text-[11px] tabular-nums text-paper-label dark:text-term-label">
          <span className="font-bold text-paper-bright dark:text-term-bright">
            {metrics.n_spins.toLocaleString()}
          </span>{" "}
          spins
        </p>
      </header>

      <dl className="grid grid-cols-2 gap-2 sm:grid-cols-[1.4fr_1fr_1fr_1fr]">
        <div className="border border-emerald-700/40 bg-emerald-700/[0.04] p-3 dark:border-emerald-500/30 dark:bg-emerald-500/[0.04]">
          <dt className="text-[10px] uppercase tracking-widest text-paper-label dark:text-term-label">
            RTP
          </dt>
          <dd className="mt-1 font-mono text-2xl font-bold tabular-nums text-emerald-700 dark:text-emerald-500">
            {(metrics.rtp.point * 100).toFixed(3)}%
          </dd>
          <dd className="text-[10px] text-paper-hint dark:text-term-hint">
            ±{(metrics.rtp.ci_half_width * 100).toFixed(3)}% @ 95% CI
          </dd>
        </div>
        <Tile
          label="Hit frequency"
          value={`${(metrics.hit_frequency * 100).toFixed(2)}%`}
        />
        <Tile
          label="Volatility"
          value={metrics.volatility.stdev.toFixed(2)}
          sub={metrics.volatility.class_}
          accent="amber"
        />
        <Tile
          label="Max win"
          value={metrics.max_win_observed.toFixed(2)}
          sub={
            metrics.max_win_bound != null
              ? `bound: ${metrics.max_win_bound.toFixed(2)}`
              : undefined
          }
        />
      </dl>

      <ReproFooter
        configHash={metrics.config_hash}
        schemaVersion={metrics.schema_version}
      />
    </section>
  );
}

function Tile({
  label,
  value,
  sub,
  accent,
}: {
  label: string;
  value: string;
  sub?: string | undefined;
  accent?: "amber" | undefined;
}) {
  return (
    <div className="border border-paper-border p-3 dark:border-term-border">
      <dt className="text-[10px] uppercase tracking-widest text-paper-label dark:text-term-label">
        {label}
      </dt>
      <dd
        className={`mt-1 font-mono text-xl font-bold tabular-nums ${
          accent === "amber"
            ? "text-amber-600 dark:text-amber-500"
            : "text-paper-bright dark:text-term-bright"
        }`}
      >
        {value}
      </dd>
      {sub && (
        <dd className="text-[10px] text-paper-hint dark:text-term-hint">
          {sub}
        </dd>
      )}
    </div>
  );
}

function ReproFooter({
  configHash,
  schemaVersion,
}: {
  configHash: string;
  schemaVersion: string;
}) {
  const [copied, setCopied] = useState(false);
  const short =
    configHash.length > 16
      ? `${configHash.slice(0, 8)}…${configHash.slice(-6)}`
      : configHash;
  return (
    <div className="mt-3 flex items-center justify-between border-t border-paper-border pt-2 text-[11px] text-paper-label dark:border-term-border dark:text-term-label">
      <span className="inline-flex items-center gap-1.5 font-mono">
        config_hash{" "}
        <span className="font-bold text-paper-bright dark:text-term-bright">
          {short}
        </span>
        <button
          type="button"
          aria-label="Copy config hash"
          title="Copy full config hash"
          className="px-1 text-paper-text hover:text-paper-bright dark:text-term-text dark:hover:text-term-bright"
          onClick={() => {
            navigator.clipboard
              ?.writeText(configHash)
              .then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
              })
              .catch(() => {});
          }}
        >
          {copied ? "✓" : "⧉"}
        </button>
      </span>
      <span>
        schema{" "}
        <span className="font-bold text-paper-bright dark:text-term-bright">
          {schemaVersion}
        </span>{" "}
        · replay-exact
      </span>
    </div>
  );
}
